import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import PDFHeader from './PDFHeader';

// Use Helvetica (built-in) so PDF generates without custom font loading issues
const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 11, fontFamily: 'Helvetica', color: '#212529', backgroundColor: '#FFFFFF' },
  container: { width: 520, alignSelf: 'center' }, // approx 520pt max width, centered
  // Top-right details under the title
  detailsBlock: { alignSelf: 'flex-end', width: '60%', marginBottom: 10, backgroundColor: '#F9FAFB', padding: 10, borderRadius: 6, border: '1px solid #E5E7EB' },
  detailsRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', gap: 12, marginBottom: 8 },
  // blocks/cards
  card: { flex: 1, border: '1px solid #E5E7EB', borderRadius: 6, padding: 10, backgroundColor: '#FFFFFF' },
  cardTitle: { color: '#1E40AF', fontWeight: 700, fontSize: 12, marginBottom: 6, borderBottom: '1px solid #E5E7EB', paddingBottom: 4 },
  label: { color: '#6B7280' },
  value: { fontWeight: 700, color: '#1F2937' },
  sectionTitle: { color: '#1E40AF', fontWeight: 700, marginTop: 8, marginBottom: 6, borderLeft: '3px solid #2563EB', paddingLeft: 8 },
  divider: { borderTop: '1px solid #E5E7EB', marginVertical: 6 },
  // table
  table: { borderTop: '1px solid #E5E7EB', marginTop: 2 },
  row: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, borderBottom: '1px solid #F3F4F6' },
  th: { fontWeight: 700, backgroundColor: '#F0F9FF' },
  itemCol: { width: '72%' },
  amtCol: { width: '28%', textAlign: 'right' },
  totalWrap: { alignItems: 'flex-end' },
  totalCard: { border: '2px solid #2563EB', borderRadius: 6, padding: 12, marginTop: 8, width: '55%', backgroundColor: '#EFF6FF' },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  totalLabel: { color: '#374151', fontWeight: 600 },
  totalValue: { fontWeight: 700, color: '#1E40AF', fontSize: 12 },
  totalRowBold: { fontFamily: 'Helvetica-Bold' },
  totalInWords: { fontStyle: 'italic', color: '#374151', marginTop: 6, paddingTop: 6, borderTop: '1px solid #E5E7EB' },
  totalInWordsBold: { fontFamily: 'Helvetica-Bold', fontStyle: 'normal' },
  footer: { marginTop: 12, textAlign: 'center' },
  muted: { color: '#6B7280', fontSize: 9 }
});

const formatAmount = (v) => new Intl.NumberFormat('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);
const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : '');

// Number to words with currency-specific suffix (ILS -> SHEKELS, INR -> RUPEES, etc.)
function numberToWords(num, currencyCode = 'INR') {
  const suffix = currencyCode === 'ILS' ? ' SHEKELS ONLY' : currencyCode === 'USD' ? ' DOLLARS ONLY' : currencyCode === 'EUR' ? ' EUROS ONLY' : currencyCode === 'GBP' ? ' POUNDS ONLY' : ' RUPEES ONLY';
  if (!num || num <= 0) return 'ZERO' + suffix;
  const a = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE', 'TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
  const b = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];
  const inWords = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
    if (n < 1000) return a[Math.floor(n / 100)] + ' HUNDRED' + (n % 100 ? ' AND ' + inWords(n % 100) : '');
    return '';
  };
  const crore = Math.floor(num / 10000000);
  const lakh = Math.floor((num % 10000000) / 100000);
  const thousand = Math.floor((num % 100000) / 1000);
  const rest = num % 1000;
  let str = '';
  if (crore) str += inWords(crore) + ' CRORE ';
  if (lakh) str += inWords(lakh) + ' LAKH ';
  if (thousand) str += inWords(thousand) + ' THOUSAND ';
  if (rest) str += inWords(rest);
  return (str.trim() + suffix).replace(/\s+/g, ' ');
}

// Phone from clinic: model uses contactNumbers[] so take first when phone not set
const clinicPhone = (obj) => (obj?.phone || (Array.isArray(obj?.contactNumbers) && obj.contactNumbers[0]) || null);

export default function InvoicePDF({ invoice, organization, clinic, currency: currencyProp, taxRate: taxRateProp, taxLabel: taxLabelProp, taxInclusive: taxInclusiveProp }) {
  const currency = currencyProp || invoice?.organization?.settings?.currency || 'INR';
  // Tax: from props (org settings) or invoice or default 18%
  const taxRate = taxRateProp ?? (invoice?.organization?.settings?.defaultTaxRate != null ? invoice.organization.settings.defaultTaxRate / 100 : 0.18);
  const taxLabel = taxLabelProp || invoice?.organization?.settings?.taxLabel || 'GST';
  // Tax inclusive: prefer org setting from API (taxInclusiveProp), then invoice.organization.settings, then invoice.taxInclusive
  const taxInclusive = (taxInclusiveProp !== undefined && taxInclusiveProp !== null)
    ? Boolean(taxInclusiveProp)
    : (invoice?.organization?.settings?.taxInclusive !== undefined && invoice?.organization?.settings?.taxInclusive !== null)
      ? Boolean(invoice.organization.settings.taxInclusive)
      : Boolean(invoice?.taxInclusive);
  // Prefer selected clinic for header and Billed By; fallback to organization
  const hasClinic = invoice?.clinic && (invoice.clinic.name || invoice.clinic.address);
  const billedBy = hasClinic ? invoice.clinic : (invoice?.organization || organization || {});
  const billedTo = invoice?.patient || invoice?.client || {};
  const billedToPatientId = billedTo?.patientId || billedTo?.id || billedTo?._id || '';
  const clinicInfo = invoice?.clinic || clinic || {};
  const billedByPhone = clinicPhone(billedBy);

  // Items: support detailed descriptions via `details`/`notes`
  const items = (invoice?.items?.length ? invoice.items :
    (invoice?.treatments?.length ? invoice.treatments : [{ description: 'Dental Services', amount: invoice?.totalAmount || 0 }])
  ).map((it, idx) => ({
    title: it.name || it.description || `Item ${idx + 1}`,
    details: it.details || it.notes || '',
    amount: it.cost || it.amount || it.unitPrice || 0
  }));
  const sumOfItems = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
  const taxPercent = Math.round((taxRate || 0.18) * 100);
  const discountAmount = Number(invoice?.discountAmount) || 0;
  const fullAmountBeforeDiscount = sumOfItems + discountAmount;

  let subtotal, taxAmount, total;
  if (taxInclusive) {
    total = sumOfItems;
    subtotal = Math.round(total / (1 + taxRate));
    taxAmount = total - subtotal;
  } else {
    subtotal = sumOfItems;
    taxAmount = Math.round(subtotal * taxRate);
    total = subtotal + taxAmount;
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          {/* Header and Billed By use same source: selected clinic or organization */}
          <PDFHeader 
            organization={billedBy}
            clinic={clinicInfo}
            documentTitle="Invoice"
            documentSubtitle="Payment Due"
          />

          {/* Details block top-right */}
          <View style={styles.detailsBlock}>
            <View style={styles.detailsRow}><Text>Invoice No #</Text><Text style={{ fontWeight: 600 }}>{invoice?.invoiceNumber || '-'}</Text></View>
            <View style={styles.detailsRow}><Text>Invoice Date</Text><Text style={{ fontWeight: 600 }}>{formatDate(invoice?.invoiceDate)}</Text></View>
            <View style={styles.detailsRow}><Text>Due Date</Text><Text style={{ fontWeight: 600 }}>{formatDate(invoice?.dueDate)}</Text></View>
          </View>

          {/* Biller & Client */}
          <View style={styles.headerRow}>
            {/* Billed By */}
            <View style={styles.card}>
              <Text style={styles.cardTitle}>Billed By</Text>
              <Text style={{ fontWeight: 700 }}>{billedBy.name || 'DentalOS'}</Text>
              {billedBy.address?.street && (
                <Text>{billedBy.address.street}</Text>
              )}
              {billedBy.address?.city || billedBy.address?.state ? (
                <Text>
                  {billedBy.address.city || ''}
                  {billedBy.address.state ? `${billedBy.address.city ? ', ' : ''}${billedBy.address.state}` : ''}
                </Text>
              ) : null}
              {billedByPhone && (
                <Text>Phone: {billedByPhone}</Text>
              )}
              {billedBy.email && (
                <Text>Email: {billedBy.email}</Text>
              )}
              {(invoice?.createdBy?.vat || billedBy.vat) && (
                <Text>VAT: {invoice?.createdBy?.vat || billedBy.vat}</Text>
              )}
            </View>

            {/* Billed To */}
            <View style={styles.card}> 
              <Text style={styles.cardTitle}>Billed To</Text>
              <Text style={{ fontWeight: 700 }}>{billedTo.name || ''}</Text>
              {billedToPatientId ? <Text>ID: {billedToPatientId}</Text> : null}
              {billedTo.address ? (
                <>
                  {billedTo.address.street ? <Text>{billedTo.address.street}</Text> : null}
                  {billedTo.address.city || billedTo.address.state ? (
                    <Text>{(billedTo.address.city || '') + (billedTo.address.state ? `, ${billedTo.address.state}` : '')}</Text>
                  ) : null}
                </>
              ) : (
                <>
                  {billedTo.email ? <Text>{billedTo.email}</Text> : null}
                  {billedTo.phone ? <Text>{billedTo.phone}</Text> : null}
                </>
              )}
            </View>
          </View>

          <View style={styles.divider} />

          {/* Items */}
          <Text style={styles.sectionTitle}>Item</Text>
          <View style={styles.table}>
            <View style={[styles.row, styles.th]}>
              <Text style={styles.itemCol}>Description</Text>
              <Text style={styles.amtCol}>Amount</Text>
            </View>
            {items.map((it, i) => (
              <View key={i} style={styles.row}>
                <View style={styles.itemCol}>
                  <Text style={{ fontWeight: 700 }}>{i + 1}. {it.title}</Text>
                  {it.details ? (
                    <>
                      {String(it.details).split(',').map((seg, idx2) => (
                        <Text key={idx2} style={[styles.muted, { marginLeft: 6 }]}>
                          {seg.trim()}
                        </Text>
                      ))}
                    </>
                  ) : null}
                </View>
                <Text style={styles.amtCol}>{formatAmount(it.amount)} {currency}</Text>
              </View>
            ))}

            {/* totals as part of table visually, with top border before subtotal */}
            <View style={{ borderTop: '1px solid #E5E7EB' }} />
          </View>

          {/* Totals */}
          <View style={styles.totalWrap}>
            <View style={styles.totalCard}>
              {discountAmount > 0 && (
                <>
                  <View style={styles.totalRow}>
                    <Text style={styles.totalLabel}>Amount (incl. {taxLabel}, before discount)</Text>
                    <Text style={styles.totalValue}>{formatAmount(fullAmountBeforeDiscount)} {currency}</Text>
                  </View>
                  <View style={styles.totalRow}>
                    <Text style={styles.totalLabel}>Discount</Text>
                    <Text style={[styles.totalValue, { color: '#059669' }]}>-{formatAmount(discountAmount)} {currency}</Text>
                  </View>
                </>
              )}
              <View style={styles.totalRow}><Text style={styles.totalLabel}>{taxInclusive ? 'Subtotal (net)' : 'Subtotal'}</Text><Text style={styles.totalValue}>{formatAmount(subtotal)} {currency}</Text></View>
              <View style={styles.totalRow}><Text style={styles.totalLabel}>{taxLabel} ({taxPercent}%)</Text><Text style={styles.totalValue}>{formatAmount(taxAmount)} {currency}</Text></View>
              <View style={[styles.totalRow, styles.totalRowBold, { marginTop: 4 }]}>
                <Text style={[styles.totalLabel, styles.totalRowBold]}>{taxInclusive ? `Total (incl. ${taxLabel})` : `Total (${currency})`}</Text>
                <Text style={[styles.totalValue, styles.totalRowBold]}>{formatAmount(total)} {currency}</Text>
              </View>
            </View>
          </View>

          {/* Total in words */}
          <View style={{ marginTop: 6 }}>
            <Text style={[styles.totalInWords, styles.totalInWordsBold]}>Total (in words): {numberToWords(Math.round(total), currency)}</Text>
          </View>

          {/* Footer */}
          <View style={styles.divider} />
          <View style={styles.footer}>
            <Text style={styles.sectionTitle}>Terms and Conditions</Text>
            {/* Render a simple ordered list */}
            <View style={{ marginTop: 2 }}>
              {(invoice?.termsList || [
                'Payment is due within the specified due date.',
                `All amounts are in ${currency}.`,
              ]).map((t, i) => (
                <Text key={i} style={styles.muted}>{i + 1}. {t}</Text>
              ))}
            </View>
            <View style={{ borderTop: '1px solid #E5E7EB', marginTop: 8, paddingTop: 6 }}>
              <Text style={{ textAlign: 'center', fontSize: 9, color: '#6B7280' }}>
                For any enquiry, reach out via email at {billedBy?.email || invoice?.contactEmail || billedBy?.contactInfo?.email || 'info@detalos.com'}, call on {billedByPhone || invoice?.contactPhone || billedBy?.contactInfo?.phone || '—'}.
              </Text>
            </View>
            <View style={{ marginTop: 8, textAlign: 'center' }}>
              <Text style={styles.muted}>This document was generated electronically by DentalOS</Text>
              <Text style={[styles.muted, { marginTop: 4 }]}>
                {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })} at {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
}


